import axios from "axios";
import { eventService } from "../../services/eventService";
import toast from "react-hot-toast";
import { cleanHtmlEntities } from "./utils/textUtils";

/**
 * Extract username from LinkedIn profile URL
 * @param {string} url - LinkedIn profile URL
 * @returns {string} Username or empty string
 */
export const extractUsername = (url) => {
  if (!url) return "";

  // Handle various LinkedIn URL formats
  const patterns = [
    /linkedin\.com\/in\/([^/?]+)/,
    /linkedin\.com\/pub\/[^/]+\/[^/]+\/[^/]+\/([^/?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return "";
};

/**
 * Fetch user's LinkedIn posts for tone analysis
 * @param {Object} userProfile - User profile with LinkedIn information
 * @param {Function} setToneAnalysisLoading - Function to set loading state
 * @param {Function} setUserLinkedInPosts - Function to set user posts
 * @param {Function} setError - Function to set error state
 * @returns {Promise<Array>} Array of LinkedIn posts
 */
export const fetchUserLinkedInPosts = async (
  userProfile,
  setToneAnalysisLoading,
  setUserLinkedInPosts,
  setError
) => {
  if (!userProfile?.linkedinProfile) {
    console.error("No LinkedIn profile found for user");
    return [];
  }

  try {
    setToneAnalysisLoading(true);
    const username = extractUsername(userProfile.linkedinProfile);

    if (!username) {
      console.error("Could not extract username from LinkedIn profile");
      return [];
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/linkedin/profile/posts`,
      {
        params: { username },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    let postsData = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        postsData = response.data;
      } else if (response.data.posts && Array.isArray(response.data.posts)) {
        postsData = response.data.posts;
      }
    }
    setUserLinkedInPosts(postsData);
    return postsData;
  } catch (error) {
    console.error("Error fetching user LinkedIn posts:", error);
    setError("Failed to fetch your LinkedIn posts for tone analysis");
    return [];
  } finally {
    setToneAnalysisLoading(false);
  }
};

/**
 * Analyze tone using Gemini
 * @param {Array} posts - Array of LinkedIn posts
 * @param {Function} setToneAnalysisLoading - Function to set loading state
 * @param {Function} setAnalyzedTone - Function to set analyzed tone
 * @param {Function} setError - Function to set error state
 * @returns {Promise<string>} Analyzed tone
 */
export const analyzeToneWithGemini = async (
  posts,
  setToneAnalysisLoading,
  setAnalyzedTone,
  setError
) => {
  if (!posts || posts.length === 0) {
    console.error("No posts provided for tone analysis");
    return "";
  }

  try {
    setToneAnalysisLoading(true);

    // Take the most recent 10 posts for analysis (to avoid token limits)
    const postsToAnalyze = posts.slice(0, 10);
    const postContents = postsToAnalyze
      .map((post) => post.content || "")
      .filter((content) => content.trim().length > 0)
      .join("\n\n---\n\n");

    if (!postContents) {
      console.error("No post content found for tone analysis");
      return "";
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/ai/analyze-tone`,
      {
        content: postContents,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const analyzedToneResult = response.data.analysis || "";
    setAnalyzedTone(analyzedToneResult);
    return analyzedToneResult;
  } catch (error) {
    console.error("Error analyzing tone:", error);
    setError("Failed to analyze your writing tone");
    return "";
  } finally {
    setToneAnalysisLoading(false);
  }
};

/**
 * Format AI response content
 * @param {Object|string} data - Response data
 * @returns {string} Formatted content
 */
export const formatResponse = (data) => {
  let content = "";

  // Try to extract content from various response formats
  if (data.content) {
    content = data.content;
  } else if (typeof data === "string") {
    content = data;
  } else if (data.post) {
    content = data.post;
  } else if (data.text) {
    content = data.text;
  } else {
    // As a last resort, try to convert the entire response to a string
    content = JSON.stringify(data);
  }

  return content;
};

/**
 * Handle main content generation
 * @param {Object} config - Configuration object with all necessary parameters
 * @returns {Promise<string>} Generated content
 */
export const handleGenerate = async (config) => {
  const {
    formData,
    activeTab,
    imageGeneratedTopic,
    youtubeSubtitles,
    articleContent,
    templateTopic,
    selectedTemplate,
    toneAnalysisEnabled,
    userLinkedInPosts,
    analyzedTone,
    useAdvancedSettings,
    includeEmojis,
    includeHashtags,
    contentLength,
    selectedModel,
    contentPreferencesEnabled,
    setError,
    setLoading,
    setGeneratedContent,
    setGeneratedHashtags,
    setAnalyzedTone,
  } = config;

  if (!formData.topic && activeTab === "custom") {
    toast.error("Please enter a topic for the custom post");
    return;
  }
  if (!imageGeneratedTopic && activeTab === "image") {
    toast.error("Please generate a topic from an image first");
    return;
  }

  setError("");
  setLoading(true);
  let loadingToastId = null;

  try {
    loadingToastId = toast.loading("Generating your post...");
    let topicToUse = formData.topic;

    // Determine topic based on active tab
    if (activeTab === "image") {
      topicToUse = imageGeneratedTopic;
    } else if (activeTab === "youtube") {
      if (youtubeSubtitles) {
        let subtitlesText = "";
        if (
          typeof youtubeSubtitles === "string" &&
          youtubeSubtitles.includes("<text ")
        ) {
          const regex = /<text [^>]*>(.*?)<\/text>/g;
          let match;
          while ((match = regex.exec(youtubeSubtitles)) !== null) {
            subtitlesText += match[1] + " ";
          }
          subtitlesText = cleanHtmlEntities(subtitlesText);
        } else {
          subtitlesText =
            typeof youtubeSubtitles === "string"
              ? youtubeSubtitles
              : JSON.stringify(youtubeSubtitles, null, 2);
        }
        topicToUse = subtitlesText.trim() || "YouTube Video Content";
      } else {
        toast.dismiss(loadingToastId);
        toast.error("No YouTube content processed for topic");
        setLoading(false);
        return;
      }
    } else if (activeTab === "article") {
      if (articleContent) {
        topicToUse = articleContent;
      } else {
        toast.dismiss(loadingToastId);
        toast.error("No article content processed for topic");
        setLoading(false);
        return;
      }
    } else if (activeTab === "templates") {
      if (templateTopic && selectedTemplate) {
        topicToUse = templateTopic;
      } else {
        toast.error("Please select a template and enter a topic");
        setLoading(false);
        return;
      }
      console.warn(
        "handleGenerate called for templates tab, this might be unintentional."
      );
    }

    // Ensure topicToUse is not empty
    if (
      !topicToUse &&
      (activeTab === "custom" ||
        activeTab === "image" ||
        activeTab === "article" ||
        activeTab === "youtube")
    ) {
      toast.dismiss(loadingToastId);
      toast.error("A topic is required for generation.");
      setLoading(false);
      return;
    }

    // Handle tone analysis if enabled
    let toneInstruction = "";
    if (toneAnalysisEnabled) {
      if (userLinkedInPosts.length === 0 || !analyzedTone) {
        toast.dismiss(loadingToastId);
        toast.error(
          "Please wait for tone analysis to complete before generating."
        );
        setLoading(false);
        return;
      }

      toneInstruction = `\n\nIMPORTANT: Write this post in the exact same tone and style as the user's previous LinkedIn posts. Based on the analysis of their previous posts, their tone is: ${analyzedTone}. Match their writing style, voice, personality, and approach closely. For emphasis, use single quotes ('word') instead of asterisks (*word*).`;
    }

    let response;

    if (toneAnalysisEnabled && userLinkedInPosts.length > 0) {
      // Use Gemini-based generation with tone analysis
      const postsContent = userLinkedInPosts
        .map((post) => post.content)
        .join("\n\n---\n\n");

      const toneDataToSend = {
        topic: topicToUse,
        posts: postsContent,
        industry: formData.industry || "",
        role: formData.professionalRole || "",
        targetAudience: formData.targetAudience || "",
        writingStyle: formData.writingStyle || null,
        goals: formData.goals || [],
        includeEmojis: includeEmojis,
        includeHashtags: includeHashtags,
        contentLength: contentLength,
      };

      response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate-with-tone`,
        toneDataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } else {
      // Use selected model generation
      const dataToSend = {
        bio:
          topicToUse +
          toneInstruction +
          "\n\nFORMATTING: For emphasis, use single quotes ('word') instead of asterisks (*word*).",
        vibe: formData.writingStyle || null, // Always include vibe if set
        goals: formData.goals || [], // Always include goals if set
        industry: useAdvancedSettings ? formData.industry || "" : "",
        role: useAdvancedSettings ? formData.professionalRole || "" : "",
        targetAudience: useAdvancedSettings
          ? formData.targetAudience || ""
          : "",
        model: selectedModel,
        useAdvancedSettings: useAdvancedSettings,
        includeEmojis: includeEmojis,
        includeHashtags: includeHashtags,
        contentLength: contentLength,
        topic: selectedModel === "in-model-b" ? topicToUse : undefined,
      };

      response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate`,
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    }

    // Format and set the generated content
    let content;
    if (selectedModel === "in-model-b") {
      // Handle Model B response format
      content = response.data?.content || response.data;

      if (response.data?.usedTemplate) {
        console.log("Model B used template:", response.data.usedTemplate);
      }

      if (!content) {
        throw new Error("No content received from Model B API");
      }
    } else if (toneAnalysisEnabled && userLinkedInPosts.length > 0) {
      // Handle Gemini response format
      content = response.data.content || response.data;
      if (response.data.toneAnalysis) {
        setAnalyzedTone(response.data.toneAnalysis);
      }
    } else {
      // Handle InstaImage response format
      let rawContent = formatResponse(response.data);

      // Apply content preferences through Gemini post-processing
      const needsPostProcessing =
        contentPreferencesEnabled &&
        (includeEmojis === false ||
          includeHashtags === false ||
          contentLength !== "default");

      if (needsPostProcessing) {
        const postProcessData = {
          content: rawContent,
          includeEmojis,
          includeHashtags,
          contentLength,
        };

        const postProcessResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/ai/post-process-content`,
          postProcessData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        content = postProcessResponse.data.content || rawContent;
      } else {
        content = rawContent;
      }
    }

    // Set the generated content
    setGeneratedContent(content);

    // Handle hashtags if they exist
    if (response.data?.hashtags) {
      setGeneratedHashtags(response.data.hashtags);
    }

    // Emit event for credit tracking
    eventService.emit("CREDITS_UPDATED");

    toast.dismiss(loadingToastId);
    toast.success("Post generated successfully!");

    return content;
  } catch (err) {
    console.error("Generation error:", err);
    setError(err.message || "Failed to generate content. Please try again.");

    if (loadingToastId) {
      toast.dismiss(loadingToastId);
    }
    toast.error("Failed to generate content. Please try again.");
    throw err;
  } finally {
    setLoading(false);
  }
};

/**
 * Handle form field changes
 * @param {Event} e - Change event
 * @param {Object} formData - Current form data
 * @param {Function} setFormData - Function to update form data
 */
export const handleChange = (e, formData, setFormData) => {
  const { name, value } = e.target;

  if (name === "writingStyle") {
    setFormData({ ...formData, writingStyle: value });
  } else {
    const newData = { ...formData, [name]: value };
    setFormData(newData);
  }
};
